import React, { useState } from 'react';
import styled from 'styled-components';
import { Helmet } from 'react-helmet';
import { FaCode, FaCube, FaServer, FaNetworkWired, FaTerminal, FaFileContract } from 'react-icons/fa';
import SectionTitle from '../components/SectionTitle';
import Button from '../components/Button';

const PageContainer = styled.div`
  padding-top: 2rem;
  background: linear-gradient(135deg, rgba(0, 213, 146, 0.03) 0%, rgba(0, 213, 146, 0) 100%);
  position: relative;
  overflow-x: hidden;
  
  &::before {
    content: '';
    position: fixed;
    width: 100%;
    height: 100%;
    top: 0;
    left: 0;
    background-image: url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%2300d592' fill-opacity='0.02'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E");
    z-index: -1;
    pointer-events: none;
    opacity: 0.8;
  }
`;

const Section = styled.section`
  padding: 6rem 0;
  background-color: ${props => props.alternate ? 'rgba(240, 247, 255, 0.7)' : props.theme.colors.white};
  position: relative;
  overflow: hidden;
  
  ${props => props.alternate && `
    &::before {
      content: '';
      position: absolute;
      width: 100%;
      height: 100%;
      top: 0;
      left: 0;
      background: 
        radial-gradient(circle at 15% 85%, rgba(0, 213, 146, 0.08) 0%, transparent 40%),
        radial-gradient(circle at 85% 15%, rgba(0, 213, 146, 0.08) 0%, transparent 40%);
      z-index: 0;
    }
  `}
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;
  position: relative;
  z-index: 2;
`;

const HeroSection = styled.section`
  background: linear-gradient(135deg, #f0fffa 0%, #e6f7f2 100%);
  padding: 7rem 0 8rem;
  text-align: center;
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    width: 100%;
    height: 100%;
    top: 0;
    left: 0;
    background: 
      radial-gradient(circle at 20% 20%, rgba(0, 213, 146, 0.1) 0%, transparent 40%),
      radial-gradient(circle at 80% 80%, rgba(0, 213, 146, 0.1) 0%, transparent 40%),
      url("data:image/svg+xml,%3Csvg width='20' height='20' viewBox='0 0 20 20' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='%2300d592' fill-opacity='0.03' fill-rule='evenodd'%3E%3Cpath d='M0 0h20L0 20z'/%3E%3C/g%3E%3C/svg%3E");
    z-index: 0;
  }
  
  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    width: 100%;
    height: 10px;
    background: linear-gradient(90deg, 
      transparent 0%,
      ${props => props.theme.colors.primary} 50%,
      transparent 100%
    );
    opacity: 0.3;
  }
`;

const HeroTitle = styled.h1`
  font-size: 3.8rem;
  margin-bottom: 1.8rem;
  color: ${props => props.theme.colors.secondary};
  position: relative;
  z-index: 1;
  font-weight: 800;
  line-height: 1.2;
  
  span {
    color: ${props => props.theme.colors.primary};
    position: relative;
    display: inline-block;
    
    &::after {
      content: '';
      position: absolute;
      width: 100%;
      height: 8px;
      background: rgba(0, 213, 146, 0.2);
      bottom: 8px;
      left: 0;
      z-index: -1;
      border-radius: 10px;
    }
  }
  
  &::before {
    content: '</>';
    position: absolute;
    top: -20px;
    left: 50%;
    transform: translateX(-50%);
    color: rgba(0, 213, 146, 0.2);
    font-size: 1.5rem;
    font-weight: normal;
    opacity: 0.8;
    font-family: monospace;
  }
  
  @media (max-width: ${props => props.theme.breakpoints.md}) {
    font-size: 3rem;
  }
`;

const HeroDescription = styled.p`
  font-size: 1.3rem;
  margin-bottom: 2.5rem;
  max-width: 800px;
  margin-left: auto;
  margin-right: auto;
  color: ${props => props.theme.colors.gray};
  line-height: 1.7;
  position: relative;
  z-index: 1;
  
  &::after {
    content: '';
    position: absolute;
    width: 80px;
    height: 3px;
    background: linear-gradient(90deg, 
      transparent 0%,
      ${props => props.theme.colors.primary} 50%,
      transparent 100%
    );
    bottom: -15px;
    left: 50%;
    transform: translateX(-50%);
  }
  
  @media (max-width: ${props => props.theme.breakpoints.md}) {
    font-size: 1.2rem;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 1.5rem;
  justify-content: center;
  margin-top: 2.5rem;
  position: relative;
  z-index: 1;
  
  @media (max-width: ${props => props.theme.breakpoints.sm}) {
    flex-direction: column;
    align-items: center;
    gap: 1rem;
  }
`;

const ResourcesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(340px, 1fr));
  gap: 2.5rem;
  margin-top: 4rem;
  position: relative;
  z-index: 2;
`;

const ResourceCard = styled.div`
  background: linear-gradient(135deg, #ffffff 0%, #f8fcff 100%);
  border-radius: 18px;
  padding: 2.5rem;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.04);
  border: 1px solid rgba(0, 213, 146, 0.1);
  transition: all 0.4s cubic-bezier(0.165, 0.84, 0.44, 1);
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: linear-gradient(135deg, rgba(0, 213, 146, 0.05) 0%, transparent 100%);
    opacity: 0;
    transition: opacity 0.4s ease;
    z-index: 0;
  }
  
  &::after {
    content: '';
    position: absolute;
    bottom: -100px;
    right: -100px;
    width: 200px;
    height: 200px;
    background: radial-gradient(circle, rgba(0, 213, 146, 0.08) 0%, transparent 70%);
    border-radius: 50%;
    opacity: 0.5;
    transition: all 0.4s ease;
  }
  
  &:hover {
    transform: translateY(-10px) scale(1.02);
    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.08);
    border-color: rgba(0, 213, 146, 0.3);
    
    &::before {
      opacity: 1;
    }
    
    &::after {
      transform: translate(-30px, -30px);
    }
  }
`;

const ResourceIcon = styled.div`
  width: 70px;
  height: 70px;
  background: linear-gradient(135deg, ${props => props.theme.colors.primary}, #00a080);
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 1.8rem;
  color: white;
  font-size: 1.8rem;
  position: relative;
  box-shadow: 0 10px 20px rgba(0, 213, 146, 0.2);
  
  &::before {
    content: '';
    position: absolute;
    top: -5px;
    left: -5px;
    right: -5px;
    bottom: -5px;
    background: linear-gradient(135deg, ${props => props.theme.colors.primary}, #00a080);
    border-radius: 18px;
    opacity: 0.3;
    z-index: -1;
    filter: blur(8px);
  }
  
  svg {
    filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.2));
  }
`;

const ResourceTitle = styled.h3`
  font-size: 1.4rem;
  margin-bottom: 1rem;
  color: ${props => props.theme.colors.secondary};
  font-weight: 600;
  position: relative;
  
  &::after {
    content: '';
    position: absolute;
    bottom: -6px;
    left: 0;
    width: 40px;
    height: 2px;
    background-color: ${props => props.theme.colors.primary};
    border-radius: 2px;
  }
`;

const ResourceDescription = styled.p`
  color: ${props => props.theme.colors.gray};
  font-size: 1rem;
  line-height: 1.7;
  margin-bottom: 1.8rem;
  position: relative;
  z-index: 1;
`;

const ChainInfoContainer = styled.div`
  background: linear-gradient(120deg, rgba(0,213,146,0.05) 0%, rgba(255,255,255,1) 50%, rgba(0,213,146,0.05) 100%);
  border-radius: 20px;
  padding: 3rem;
  box-shadow: 0 20px 50px rgba(0, 0, 0, 0.05), 0 10px 30px rgba(0, 213, 146, 0.1);
  margin-top: 3rem;
  position: relative;
  overflow-y: hidden;
  overflow-x: auto;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(0, 213, 146, 0.2);
  transition: all 0.3s ease;
  animation: cardPulse 8s infinite alternate;
  
  @keyframes cardPulse {
    0% {
      box-shadow: 0 20px 50px rgba(0, 0, 0, 0.05), 0 10px 30px rgba(0, 213, 146, 0.1);
      border: 1px solid rgba(0, 213, 146, 0.2);
    }
    100% {
      box-shadow: 0 20px 50px rgba(0, 0, 0, 0.05), 0 10px 30px rgba(0, 213, 146, 0.25);
      border: 1px solid rgba(0, 213, 146, 0.4);
    }
  }
  
  &::before {
    content: '';
    position: absolute;
    width: 200px;
    height: 200px;
    background: radial-gradient(circle, rgba(0, 213, 146, 0.3) 0%, transparent 70%);
    top: -100px;
    right: -100px;
    border-radius: 50%;
    animation: floatGlow 15s ease-in-out infinite alternate;
  }
  
  &::after {
    content: '';
    position: absolute;
    width: 300px;
    height: 300px;
    background: radial-gradient(circle, rgba(0, 213, 146, 0.2) 0%, transparent 70%);
    bottom: -150px;
    left: -150px;
    border-radius: 50%;
    animation: floatGlow 15s ease-in-out 2s infinite alternate-reverse;
  }
  
  @keyframes floatGlow {
    0% {
      transform: translateY(0) translateX(0) scale(1);
      opacity: 0.5;
    }
    50% {
      transform: translateY(-30px) translateX(20px) scale(1.1);
      opacity: 0.7;
    }
    100% {
      transform: translateY(0) translateX(0) scale(1);
      opacity: 0.5;
    }
  }
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 25px 60px rgba(0, 0, 0, 0.07), 0 15px 40px rgba(0, 213, 146, 0.15);
    animation-play-state: paused;
  }
`;

const ChainInfoTitle = styled.h3`
  font-size: 2rem;
  margin-bottom: 2.5rem;
  color: ${props => props.theme.colors.secondary};
  text-align: center;
  position: relative;
  display: inline-block;
  left: 50%;
  transform: translateX(-50%);
  
  &::after {
    content: '';
    position: absolute;
    bottom: -10px;
    left: 0;
    width: 100%;
    height: 3px;
    background: linear-gradient(90deg, 
      rgba(0, 213, 146, 0) 0%, 
      rgba(0, 213, 146, 1) 50%, 
      rgba(0, 213, 146, 0) 100%
    );
  }
`;

const ChainInfoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 2rem;
  position: relative;
  z-index: 1;
`;

const ChainInfoItem = styled.div`
  background: rgba(255, 255, 255, 0.7);
  padding: 1.5rem;
  border-radius: 15px;
  transition: all 0.3s ease;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.03);
  border: 1px solid rgba(0, 213, 146, 0.1);
  position: relative;
  overflow: hidden;
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 10px 25px rgba(0, 0, 0, 0.05), 0 5px 15px rgba(0, 213, 146, 0.1);
    border-color: rgba(0, 213, 146, 0.3);
  }
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: linear-gradient(135deg, rgba(0, 213, 146, 0.1) 0%, transparent 100%);
    opacity: 0;
    transition: opacity 0.3s ease;
  }
  
  &:hover::before {
    opacity: 1;
  }
`;

const ChainInfoLabel = styled.h4`
  font-size: 0.9rem;
  text-transform: uppercase;
  letter-spacing: 1px;
  color: ${props => props.theme.colors.primary};
  margin-bottom: 0.8rem;
  font-weight: 600;
  display: flex;
  align-items: center;
  
  &::before {
    content: '';
    display: inline-block;
    width: 8px;
    height: 8px;
    background-color: ${props => props.theme.colors.primary};
    border-radius: 50%;
    margin-right: 10px;
  }
`;

const ChainInfoValue = styled.div`
  font-size: 1.2rem;
  color: ${props => props.theme.colors.secondary};
  font-weight: 500;
  word-break: break-all;
  position: relative;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  
  code {
    background-color: #f8f9fa;
    padding: 0.5rem 0.8rem;
    border-radius: 8px;
    font-family: 'Fira Code', monospace;
    font-size: 0.95rem;
    color: #333;
    border: 1px solid rgba(0, 213, 146, 0.2);
    display: inline-block;
    margin-top: 0.3rem;
    width: 100%;
    box-shadow: inset 0 1px 3px rgba(0, 0, 0, 0.05);
  }
`;

const CopyButton = styled.button`
  background-color: rgba(0, 213, 146, 0.1);
  border: 1px solid rgba(0, 213, 146, 0.2);
  border-radius: 8px;
  padding: 0.4rem 0.8rem;
  font-size: 0.8rem;
  margin-left: 0.8rem;
  cursor: pointer;
  transition: all 0.2s ease;
  color: ${props => props.theme.colors.primary};
  font-weight: 500;
  display: flex;
  align-items: center;
  
  &:hover {
    background-color: ${props => props.theme.colors.primary};
    color: white;
    transform: translateY(-2px);
    box-shadow: 0 3px 10px rgba(0, 213, 146, 0.2);
  }
  
  &:active {
    transform: translateY(0);
  }
  
  &::after {
    content: "📋";
    margin-left: 5px;
    font-size: 0.9rem;
  }
`;

const CodeExampleContainer = styled.div`
  margin-top: 5rem;
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    width: 200px;
    height: 200px;
    background: radial-gradient(circle, rgba(0, 213, 146, 0.08) 0%, transparent 70%);
    border-radius: 50%;
    top: -100px;
    left: 10%;
    z-index: 0;
    opacity: 0.7;
  }
`;

const TabsContainer = styled.div`
  display: flex;
  margin-bottom: 2rem;
  position: relative;
  z-index: 1;
  border-bottom: 1px solid rgba(0, 213, 146, 0.1);
  flex-wrap: wrap;
  justify-content: center;
  
  @media (max-width: ${props => props.theme.breakpoints.md}) {
    justify-content: flex-start;
    overflow-x: auto;
    padding-bottom: 5px;
  }
`;

const Tab = styled.button`
  padding: 1rem 2rem;
  border: none;
  background-color: transparent;
  cursor: pointer;
  font-weight: ${props => props.active ? '700' : '500'};
  color: ${props => props.active ? props.theme.colors.primary : props.theme.colors.secondary};
  border-bottom: 3px solid ${props => props.active ? props.theme.colors.primary : 'transparent'};
  transition: all 0.3s ease;
  font-size: 1.05rem;
  position: relative;
  
  &::after {
    content: '';
    position: absolute;
    width: 0;
    height: 3px;
    bottom: -3px;
    left: 50%;
    background-color: ${props => props.theme.colors.primary};
    transition: all 0.3s ease;
    opacity: 0.5;
  }
  
  &:hover {
    color: ${props => props.theme.colors.primary};
    
    &::after {
      width: 100%;
      left: 0;
    }
  }
  
  ${props => props.active && `
    &::before {
      content: '';
      position: absolute;
      top: -10px;
      left: 50%;
      transform: translateX(-50%);
      border-left: 8px solid transparent;
      border-right: 8px solid transparent;
      border-top: 8px solid rgba(0, 213, 146, 0.2);
    }
  `}
`;

const CodeBlock = styled.div`
  background: linear-gradient(135deg, #f8fcff 0%, #f5f7fa 100%);
  border-radius: 16px;
  padding: 2rem;
  overflow-x: auto;
  position: relative;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.05);
  border: 1px solid rgba(0, 213, 146, 0.1);
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 30px;
    background: linear-gradient(90deg, 
      rgba(0, 213, 146, 0.1) 0%, 
      rgba(0, 213, 146, 0.05) 100%
    );
    border-top-left-radius: 16px;
    border-top-right-radius: 16px;
  }
  
  &::after {
    content: '';
    position: absolute;
    top: 10px;
    left: 15px;
    width: 12px;
    height: 12px;
    background-color: rgba(0, 213, 146, 0.3);
    border-radius: 50%;
    box-shadow: 25px 0 0 rgba(0, 213, 146, 0.2), 50px 0 0 rgba(0, 213, 146, 0.1);
  }
  
  pre {
    margin: 0;
    margin-top: 20px;
    padding-top: 10px;
    color: #333;
    font-family: 'Fira Code', monospace;
    font-size: 0.95rem;
    line-height: 1.6;
  }
`;

const Developers = () => {
  const [activeTab, setActiveTab] = React.useState('web3js');

  const handleTabChange = (tab) => {
    setActiveTab(tab);
  };

  const [copiedText, setCopiedText] = useState(null);

  const handleCopy = (text) => {
    navigator.clipboard.writeText(text);
    setCopiedText(text);

    // Reset the copied state after 2 seconds
    setTimeout(() => {
      setCopiedText(null);
    }, 2000);
  };

  const renderCodeExample = () => {
    switch (activeTab) {
      case 'web3js':
        return (
          <CodeBlock>
            <pre>{`// Connect to RDT Chain with Web3.js
const Web3 = require('web3');

// Initialize connection to RDT Chain
const web3 = new Web3('https://rpc-supernova.rdtchain.com');

// Check connection
web3.eth.getBlockNumber()
  .then(blockNumber => {
    console.log('Current block number:', blockNumber);
  })
  .catch(error => {
    console.error('Error connecting to RDT Chain:', error);
  });

// Send a transaction
async function sendTransaction() {
  const accounts = await web3.eth.getAccounts();
  const sender = accounts[0];
  
  const transaction = {
    from: sender,
    to: '0xRecipientAddress',
    value: web3.utils.toWei('1', 'ether'),
    gas: 21000
  };
  
  try {
    const receipt = await web3.eth.sendTransaction(transaction);
    console.log('Transaction receipt:', receipt);
  } catch (error) {
    console.error('Error sending transaction:', error);
  }
}`}</pre>
          </CodeBlock>
        );

      case 'ethersjs':
        return (
          <CodeBlock>
            <pre>{`// Connect to RDT Chain with Ethers.js
const { ethers } = require('ethers');

// Initialize connection to RDT Chain
const provider = new ethers.providers.JsonRpcProvider('https://rpc-supernova.rdtchain.com');

// Check connection
async function checkConnection() {
  try {
    const blockNumber = await provider.getBlockNumber();
    console.log('Current block number:', blockNumber);
    
    const network = await provider.getNetwork();
    console.log('Connected to network:', network.name, 'Chain ID:', network.chainId);
  } catch (error) {
    console.error('Error connecting to RDT Chain:', error);
  }
}

// Send a transaction
async function sendTransaction() {
  const privateKey = 'YOUR_PRIVATE_KEY'; // Replace with your private key
  const wallet = new ethers.Wallet(privateKey, provider);
  
  const transaction = {
    to: '0xRecipientAddress',
    value: ethers.utils.parseEther('1.0')
  };
  
  try {
    const tx = await wallet.sendTransaction(transaction);
    console.log('Transaction hash:', tx.hash);
    
    const receipt = await tx.wait();
    console.log('Transaction confirmed in block:', receipt.blockNumber);
  } catch (error) {
    console.error('Error sending transaction:', error);
  }
}

checkConnection();`}</pre>
          </CodeBlock>
        );

      case 'truffle':
        return (
          <CodeBlock>
            <pre>{`// truffle-config.js configuration for RDT Chain

const HDWalletProvider = require('@truffle/hdwallet-provider');
require('dotenv').config();

const mnemonic = process.env.MNEMONIC;

module.exports = {
  networks: {
    development: {
      host: "127.0.0.1",
      port: 8545,
      network_id: "*"
    },
    rdtchain: {
      provider: () => new HDWalletProvider(mnemonic, 'https://rpc-supernova.rdtchain.com'),
      network_id: 875423,
      gas: 5500000,
      confirmations: 2,
      timeoutBlocks: 200,
      skipDryRun: true
    }
  },
  compilers: {
    solc: {
      version: "0.8.17",
      settings: {
        optimizer: {
          enabled: true,
          runs: 200
        }
      }
    }
  }
};

// Deploy contract to RDT Chain:
// truffle migrate --network rdtchain`}</pre>
          </CodeBlock>
        );

      case 'hardhat':
        return (
          <CodeBlock>
            <pre>{`// hardhat.config.js configuration for RDT Chain

require('@nomiclabs/hardhat-waffle');
require('dotenv').config();

const PRIVATE_KEY = process.env.PRIVATE_KEY;

module.exports = {
  solidity: "0.8.17",
  networks: {
    hardhat: {},
    rdtchain: {
      url: 'https://rpc-supernova.rdtchain.com',
      accounts: [PRIVATE_KEY],
      chainId: 875423,
      gas: 5500000,
      gasPrice: 0
    }
  },
  paths: {
    sources: "./contracts",
    tests: "./test",
    cache: "./cache",
    artifacts: "./artifacts"
  }
};

// Deploy contract to RDT Chain:
// npx hardhat run scripts/deploy.js --network rdtchain`}</pre>
          </CodeBlock>
        );

      default:
        return null;
    }
  };

  return (
    <PageContainer>
      <Helmet>
        <title>Developers | RDT Chain - APIs, SDKs & Development Resources</title>
      </Helmet>
      <HeroSection>
        <Container>
          <HeroTitle>
            Build on <span>RDT Chain</span>
          </HeroTitle>
          <HeroDescription>
            Leverage the power of a fast, scalable, and fee-less blockchain to create decentralized applications
            that can transform cross-border payments and beyond.
          </HeroDescription>
          <ButtonGroup>
            <Button href="https://explorer.rdtchain.com" target="_blank" rel="noopener noreferrer">
              Explorer
            </Button>
            <Button href="https://docs.web3js.org/" target="_blank" rel="noopener noreferrer" secondary>
              Web3 JS
            </Button>
          </ButtonGroup>
        </Container>
      </HeroSection>

      <Section>
        <Container>
          <SectionTitle
            subtitle="Developer Resources"
            title="Everything You Need to Start Building"
            description="Access comprehensive resources and tools to help you build and deploy on RDT Chain."
            center
          />

          <ResourcesGrid>
            <ResourceCard>
              <ResourceIcon>
                <FaCode />
              </ResourceIcon>
              <ResourceTitle>API Reference</ResourceTitle>
              <ResourceDescription>
                Comprehensive JSON-RPC API documentation with complete method references for interacting with RDT Chain nodes.
              </ResourceDescription>
              <Button href="https://ethereum.org/en/developers/docs/apis/json-rpc/" target="_blank" rel="noopener noreferrer" small>
                View API
              </Button>
            </ResourceCard>

            <ResourceCard>
              <ResourceIcon>
                <FaFileContract />
              </ResourceIcon>
              <ResourceTitle>Smart Contract Development</ResourceTitle>
              <ResourceDescription>
                Deploy Solidity smart contracts on RDT Chain with zero gas fees using standard Ethereum development tools.
              </ResourceDescription>
              <Button href="https://ethereum.org/en/developers/docs/smart-contracts/" target="_blank" rel="noopener noreferrer" small>
                Contract Guide
              </Button>
            </ResourceCard>

            <ResourceCard>
              <ResourceIcon>
                <FaNetworkWired />
              </ResourceIcon>
              <ResourceTitle>Network Architecture</ResourceTitle>
              <ResourceDescription>
                Technical details of RDT Chain's Proof of Authority consensus mechanism, block validation, and network topography.
              </ResourceDescription>
              <Button href="https://ethereum.org/en/developers/docs/consensus-mechanisms/pos/" target="_blank" rel="noopener noreferrer" small>
                Network Details
              </Button>
            </ResourceCard>

            <ResourceCard>
              <ResourceIcon>
                <FaTerminal />
              </ResourceIcon>
              <ResourceTitle>CLI Tools</ResourceTitle>
              <ResourceDescription>
                Command line interface tools for interacting with the RDT Chain network, managing accounts, and debugging contracts.
              </ResourceDescription>
              <Button href="https://geth.ethereum.org/docs/interface/command-line-options" target="_blank" rel="noopener noreferrer" small>
                CLI Reference
              </Button>
            </ResourceCard>

            <ResourceCard>
              <ResourceIcon>
                <FaCube />
              </ResourceIcon>
              <ResourceTitle>Transaction Types</ResourceTitle>
              <ResourceDescription>
                Technical specifications for different transaction types supported on RDT Chain, including zero-fee transaction mechanics.
              </ResourceDescription>
              <Button href="https://ethereum.org/en/developers/docs/transactions/" target="_blank" rel="noopener noreferrer" small>
                Transaction Specs
              </Button>
            </ResourceCard>

            <ResourceCard>
              <ResourceIcon>
                <FaServer />
              </ResourceIcon>
              <ResourceTitle>Cross-Chain Integration</ResourceTitle>
              <ResourceDescription>
                Learn how to build applications that interact across multiple blockchain networks using RDT Chain as a fast, fee-less payment layer.
              </ResourceDescription>
              <Button href="https://ethereum.org/en/developers/docs/bridges/" target="_blank" rel="noopener noreferrer" small>
                Integration Guide
              </Button>
            </ResourceCard>
          </ResourcesGrid>
        </Container>
      </Section>

      <Section alternate>
        <Container>
          <SectionTitle
            subtitle="Network Information"
            title="RDT Chain Specifications"
            description="Essential information for connecting to and developing on RDT Chain."
            center
          />

          <ChainInfoContainer>
            <div style={{ position: 'absolute', top: '20px', right: '20px', fontSize: '1.8rem', color: 'rgba(0, 213, 146, 0.3)' }}>🔗</div>
            <div style={{ position: 'absolute', bottom: '20px', left: '20px', fontSize: '1.8rem', color: 'rgba(0, 213, 146, 0.3)' }}>⚡</div>

            <ChainInfoTitle>Network Details</ChainInfoTitle>
            <div style={{
              textAlign: 'center',
              marginBottom: '2rem',
              color: '#6c757d',
              maxWidth: '700px',
              margin: '0 auto 2.5rem'
            }}>
              Essential parameters for connecting to and developing on RDT Chain.
              Click the copy button to easily add these values to your application.
            </div>
            <ChainInfoGrid>
              <ChainInfoItem>
                <ChainInfoLabel>Network Name</ChainInfoLabel>
                <ChainInfoValue>
                  RDT Chain
                  <CopyButton onClick={() => handleCopy('RDT Chain')}>{copiedText === 'RDT Chain' ? 'Copied!' : 'Copy'}</CopyButton>
                </ChainInfoValue>
              </ChainInfoItem>

              <ChainInfoItem>
                <ChainInfoLabel>Chain ID</ChainInfoLabel>
                <ChainInfoValue>
                  875423
                  <CopyButton onClick={() => handleCopy('875423')}>{copiedText === '875423' ? 'Copied!' : 'Copy'}</CopyButton>
                </ChainInfoValue>
              </ChainInfoItem>

              <ChainInfoItem>
                <ChainInfoLabel>Currency Symbol</ChainInfoLabel>
                <ChainInfoValue>
                  RDT
                  <CopyButton onClick={() => handleCopy('RDT')}>{copiedText === 'RDT' ? 'Copied!' : 'Copy'}</CopyButton>
                </ChainInfoValue>
              </ChainInfoItem>

              <ChainInfoItem>
                <ChainInfoLabel>Block Time</ChainInfoLabel>
                <ChainInfoValue>5 seconds</ChainInfoValue>
              </ChainInfoItem>

              <ChainInfoItem>
                <ChainInfoLabel>Consensus</ChainInfoLabel>
                <ChainInfoValue>Proof of Authority (PoA)</ChainInfoValue>
              </ChainInfoItem>

              <ChainInfoItem>
                <ChainInfoLabel>EVM Compatible</ChainInfoLabel>
                <ChainInfoValue>Yes</ChainInfoValue>
              </ChainInfoItem>

              <ChainInfoItem>
                <ChainInfoLabel>RPC Endpoint</ChainInfoLabel>
                <ChainInfoValue>
                  <code>https://rpc-supernova.rdtchain.com</code>
                  <CopyButton onClick={() => handleCopy('https://rpc-supernova.rdtchain.com')}>{copiedText === 'https://rpc-supernova.rdtchain.com' ? 'Copied!' : 'Copy'}</CopyButton>
                </ChainInfoValue>
              </ChainInfoItem>

              <ChainInfoItem>
                <ChainInfoLabel>Explorer URL</ChainInfoLabel>
                <ChainInfoValue>
                  <code>https://explorer.rdtchain.com</code>
                  <CopyButton onClick={() => handleCopy('https://explorer.rdtchain.com')}>{copiedText === 'https://explorer.rdtchain.com' ? 'Copied!' : 'Copy'}</CopyButton>
                </ChainInfoValue>
              </ChainInfoItem>
            </ChainInfoGrid>
          </ChainInfoContainer>
        </Container>
      </Section>

      <Section>
        <Container>
          <SectionTitle
            subtitle="Code Examples"
            title="Start Building on RDT Chain"
            description="Get started with these code examples using popular Ethereum development frameworks."
            center
          />

          <CodeExampleContainer>
            <TabsContainer>
              <Tab
                active={activeTab === 'web3js'}
                onClick={() => handleTabChange('web3js')}
              >
                Web3.js
              </Tab>
              <Tab
                active={activeTab === 'ethersjs'}
                onClick={() => handleTabChange('ethersjs')}
              >
                Ethers.js
              </Tab>
              <Tab
                active={activeTab === 'truffle'}
                onClick={() => handleTabChange('truffle')}
              >
                Truffle
              </Tab>
              <Tab
                active={activeTab === 'hardhat'}
                onClick={() => handleTabChange('hardhat')}
              >
                Hardhat
              </Tab>
            </TabsContainer>

            {renderCodeExample()}
          </CodeExampleContainer>
        </Container>
      </Section>
    </PageContainer>
  );
};

export default Developers;